import * as XLSX from 'xlsx';
import { protectInsIdsInText, restoreProtectedInsIds } from './ins-id.util';
import {
  hasCompleteS3FilePaths,
  splitMarkdownByS3Paths,
  extractFileNameFromS3Path,
  formatDisplayFileName,
  type AgentFileType,
} from './s3-file-attachment.util';

export const MIN_COLUMN_WIDTH = 144;
export const MIN_ROW_HEIGHT = 40;
export const RESIZE_MIN_DOCUMENTS_COLUMN = 432;
export const RESIZE_MIN_PROSE_COLUMN = 312;
/** Default fixed width per column while streaming (before user drag-resize). */
export const DEFAULT_STREAMING_COLUMN_WIDTH = 168;
/** Cap for documents/prose default widths*/
const DEFAULT_DOCUMENTS_COLUMN_CAP = 288;
const DEFAULT_PROSE_COLUMN_CAP = 240;

const MAX_PARSED_MARKDOWN_SEGMENTS = 64;

export interface ParsedTableSection {
  before: string;
  title: string | null;
  hasTitle: boolean;
  tableMarkdown: string;
  after: string;
}

export interface ParsedMarkdownTableData {
  columns: readonly string[];
  rows: readonly (readonly string[])[];
}

export type MarkdownSegment =
  | { kind: 'markdown'; content: string }
  | {
      kind: 'table';
      title: string | null;
      hasTitle: boolean;
      tableMarkdown: string;
      tableData: ParsedMarkdownTableData | null;
    }
  | {
      kind: 'fileAttachment';
      s3Path: string;
      presignedUrl?: string;
      fileName: string;
      displayFileName: string;
      fileType: AgentFileType;
    };

export interface ParseAgentMarkdownSegmentsOptions {
  enableS3Attachments?: boolean;
  isStreaming?: boolean;
  /** S3 paths from history metadata when answer text has no inline file references. */
  filePathsInOrder?: readonly string[];
}

function buildFileAttachmentFromS3Path(
  s3Path: string,
  base?: Extract<MarkdownSegment, { kind: 'fileAttachment' }>
): Extract<MarkdownSegment, { kind: 'fileAttachment' }> {
  const fileName = extractFileNameFromS3Path(s3Path);
  return {
    kind: 'fileAttachment',
    s3Path,
    fileName: base?.fileName || fileName,
    displayFileName: base?.displayFileName || formatDisplayFileName(fileName),
    fileType: base?.fileType ?? ('pdf'),
  };
}

/** Bind history metadata S3 paths to file cards so preview/download can fetch fresh presigned URLs. */
function applyHistoryFilePaths(
  segments: MarkdownSegment[],
  filePathsInOrder: readonly string[]
): MarkdownSegment[] {
  const paths = filePathsInOrder
    .map((path) => path.trim())
    .filter((path) => path.length > 0);

  if (!paths.length) {
    return segments;
  }

  let fileIndex = 0;
  const updated = segments.map((segment) => {
    if (segment.kind !== 'fileAttachment') {
      return segment;
    }
    const s3Path = paths[fileIndex];
    fileIndex += 1;
    if (!s3Path) {
      return segment;
    }
    return buildFileAttachmentFromS3Path(s3Path, segment);
  });

  const remaining = paths.slice(fileIndex);
  if (!remaining.length) {
    return updated;
  }

  return [
    ...updated,
    ...remaining.map((s3Path) => buildFileAttachmentFromS3Path(s3Path)),
  ];
}

export function isTableLine(line: string): boolean {
  return line.includes('|');
}

export function isTableSeparatorLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return false;

  const noLeadingPipe = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
  const core = noLeadingPipe.endsWith('|') ? noLeadingPipe.slice(0, -1) : noLeadingPipe;
  const cells = core.split('|');
  if (cells.length < 2) return false;

  return cells.every((cell) => {
    const value = cell.trim();
    if (value.length === 0) return false;

    let hasDash = false;
    for (const ch of value) {
      if (ch === '-') {
        hasDash = true;
        continue;
      }
      if (ch !== ':' && ch !== ' ') return false;
    }
    return hasDash;
  });
}

export function isDocumentsHeavyColumn(columnName: string): boolean {
  const n = columnName.toLowerCase();
  return n.includes('required') && (n.includes('document') || n.includes('documents'));
}

export function isProseHeavyColumn(columnName: string): boolean {
  return columnName.toLowerCase().includes('change item');
}

export function columnHeaderDensityClass(columnName: string): string {
  if (isDocumentsHeavyColumn(columnName)) return 'rich-table__th--density-documents';
  if (isProseHeavyColumn(columnName)) return 'rich-table__th--density-prose';
  return '';
}

export function columnCellDensityClass(columnName: string): string {
  if (isDocumentsHeavyColumn(columnName)) return 'rich-table__td--density-documents';
  if (isProseHeavyColumn(columnName)) return 'rich-table__td--density-prose';
  return '';
}

function isMarkdownHeadingLine(line: string): boolean {
  return /^\s{0,3}#{1,6}\s+\S/.test(line);
}

function normalizeHeaderTitle(line: string): string {
  return line.replace(/^#+\s*/, '').replace(/^[-*+]\s*/, '').trim();
}

const MD_TABLE_PIPE = '|';
const MD_ESCAPE_CHAR = '\\';
/** Private-use placeholder so flattened-row rehydration does not split on GFM `\|`. */
const ESCAPED_PIPE_PLACEHOLDER = '\uE002';

function isMarkdownTableDelimiter(line: string, index: number): boolean {
  return line[index] === MD_TABLE_PIPE && line[index - 1] !== MD_ESCAPE_CHAR;
}

/** Turns GFM/JSON-escaped `\|` / `\\|` runs into a literal `|` inside a cell. */
function unescapeMarkdownTablePipes(cell: string): string {
  let result = '';
  let index = 0;
  while (index < cell.length) {
    if (cell[index] !== MD_ESCAPE_CHAR) {
      result += cell[index];
      index += 1;
      continue;
    }
    const slashStart = index;
    while (index < cell.length && cell[index] === MD_ESCAPE_CHAR) {
      index += 1;
    }
    if (index < cell.length && cell[index] === MD_TABLE_PIPE) {
      result += MD_TABLE_PIPE;
      index += 1;
      continue;
    }
    result += cell.slice(slashStart, index);
  }
  return result;
}

function protectEscapedMarkdownPipes(text: string): string {
  let result = '';
  for (let index = 0; index < text.length; index += 1) {
    if (text[index] === MD_ESCAPE_CHAR && text[index + 1] === MD_TABLE_PIPE) {
      result += ESCAPED_PIPE_PLACEHOLDER;
      index += 1;
      continue;
    }
    result += text[index];
  }
  return result;
}

function restoreEscapedMarkdownPipes(text: string): string {
  return text.split(ESCAPED_PIPE_PLACEHOLDER).join(`${MD_ESCAPE_CHAR}${MD_TABLE_PIPE}`);
}

export function parsePipeRow(line: string): string[] {
  const trimmed = line.trim();
  const cells: string[] = [];
  let current = '';
  let endedOnDelimiter = false;
  const start = trimmed.startsWith(MD_TABLE_PIPE) ? 1 : 0;

  for (let index = start; index < trimmed.length; index += 1) {
    if (isMarkdownTableDelimiter(trimmed, index)) {
      cells.push(unescapeMarkdownTablePipes(current).trim());
      current = '';
      endedOnDelimiter = true;
      continue;
    }
    current += trimmed[index];
    endedOnDelimiter = false;
  }

  if (!endedOnDelimiter) {
    cells.push(unescapeMarkdownTablePipes(current).trim());
  }

  return cells;
}

/** Drops a trailing empty cell from `| a | b | |` style pipe rows. */
export function trimTrailingEmptyPipeCells(cells: readonly string[]): string[] {
  const trimmed = [...cells];
  while (trimmed.length > 1 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop();
  }
  return trimmed;
}

export function rehydrateFlattenedTableRows(markdown: string): string {
  let normalized = markdown.replace(/\r\n/g, '\n');
  const seemsLikeFlattenedTable = normalized.includes('|') && normalized.includes('| ---');
  if (seemsLikeFlattenedTable) {
    const withProtectedPipes = protectEscapedMarkdownPipes(normalized);
    normalized = restoreEscapedMarkdownPipes(withProtectedPipes.replace(/\|\s+\|/g, '|\n|'));
  }
  return normalized;
}

export function parseMarkdownTableData(tableMarkdown: string): ParsedMarkdownTableData | null {
  const tableLines = tableMarkdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && isTableLine(line));

  if (tableLines.length < 2) return null;

  const columns = trimTrailingEmptyPipeCells(parsePipeRow(tableLines[0] ?? ''));
  if (columns.length === 0) return null;

  const rows = tableLines
    .slice(2)
    .map((line) => trimTrailingEmptyPipeCells(parsePipeRow(line)))
    .filter((row) => row.some((cell) => cell.length > 0))
    .map((row) => {
      if (row.length >= columns.length) {
        return row.slice(0, columns.length);
      }
      return [...row, ...new Array(columns.length - row.length).fill('')];
    });

  return { columns, rows };
}

export function parseFirstTableSection(markdown: string): ParsedTableSection | null {
  const lines = markdown.split('\n');
  let tableStart = -1;

  for (let i = 0; i < lines.length - 1; i += 1) {
    const line = lines[i];
    const nextLine = lines[i + 1];
    if (line === undefined || nextLine === undefined) continue;
    if (!isTableLine(line)) continue;
    if (!isTableSeparatorLine(nextLine)) continue;
    tableStart = i;
    break;
  }

  if (tableStart < 0) return null;

  let tableEnd = tableStart + 2;
  while (tableEnd < lines.length) {
    const line = lines[tableEnd];
    if (line === undefined || line.trim().length === 0 || !isTableLine(line)) break;
    tableEnd += 1;
  }

  const nonEmptyBeforeTable: number[] = [];
  for (let i = 0; i < tableStart; i += 1) {
    const line = lines[i];
    if (line === undefined || line.trim().length === 0) continue;
    nonEmptyBeforeTable.push(i);
  }

  const closestBeforeTable = nonEmptyBeforeTable[nonEmptyBeforeTable.length - 1] ?? null;
  const titleIndex =
    closestBeforeTable !== null && isMarkdownHeadingLine(lines[closestBeforeTable] ?? '')
      ? closestBeforeTable
      : null;

  const beforeLines =
    titleIndex === null
      ? lines.slice(0, tableStart)
      : [...lines.slice(0, titleIndex), ...lines.slice(titleIndex + 1, tableStart)];
  const before = beforeLines.join('\n').trim();
  const tableMarkdown = lines.slice(tableStart, tableEnd).join('\n').trim();
  const after = lines.slice(tableEnd).join('\n').trim();
  const titleRaw = titleIndex === null ? null : (lines[titleIndex] ?? null);
  const normalizedTitle = titleRaw === null ? '' : normalizeHeaderTitle(titleRaw);
  const title = normalizedTitle.length > 0 ? normalizedTitle : null;
  const hasTitle = title !== null;

  if (tableMarkdown.length === 0) return null;
  return { before, title, hasTitle, tableMarkdown, after };
}

export function parseAllMarkdownSegments(markdown: string): readonly MarkdownSegment[] {
  const segments: MarkdownSegment[] = [];
  let remaining = markdown;
  let budget = MAX_PARSED_MARKDOWN_SEGMENTS;
  while (remaining.length > 0 && budget > 0) {
    budget -= 1;
    const section = parseFirstTableSection(remaining);
    if (section === null) {
      if (remaining.trim().length > 0) {
        segments.push({ kind: 'markdown', content: remaining });
      }
      return segments;
    }
    if (section.before.trim().length > 0) {
      segments.push({ kind: 'markdown', content: section.before });
    }
    segments.push({
      kind: 'table',
      title: section.title,
      hasTitle: section.hasTitle,
      tableMarkdown: section.tableMarkdown,
      tableData: parseMarkdownTableData(section.tableMarkdown),
    });
    remaining = section.after;
  }
  if (remaining.trim().length > 0) {
    segments.push({ kind: 'markdown', content: remaining });
  }
  return segments;
}

export function hasTableSegments(markdown: string): boolean {
  return parseAllMarkdownSegments(rehydrateFlattenedTableRows(markdown)).some((s) => s.kind === 'table');
}

export function hasS3FileAttachmentSegments(
  markdown: string,
  isStreaming = false
): boolean {
  return hasCompleteS3FilePaths(markdown, isStreaming);
}

function expandMarkdownWithS3Attachments(
  segment: Extract<MarkdownSegment, { kind: 'markdown' }>,
  isStreaming: boolean
): MarkdownSegment[] {
  const parts = splitMarkdownByS3Paths(segment.content, isStreaming);
  if (parts.length === 1 && parts[0].kind === 'markdown') {
    return [segment];
  }
  return parts.map((part) =>
    part.kind === 'markdown'
      ? { kind: 'markdown' as const, content: part.content }
      : {
          kind: 'fileAttachment' as const,
          s3Path: part.s3Path,
          presignedUrl: part.presignedUrl,
          fileName: part.fileName,
          displayFileName: part.displayFileName,
          fileType: part.fileType,
        }
  );
}

export function parseAgentMarkdownSegments(
  markdown: string,
  options: ParseAgentMarkdownSegmentsOptions = {}
): readonly MarkdownSegment[] {
  const { enableS3Attachments = false, isStreaming = false } = options;
  const normalized = rehydrateFlattenedTableRows(markdown);
  const baseSegments = parseAllMarkdownSegments(normalized);

  if (!enableS3Attachments) {
    return baseSegments;
  }

  const expanded: MarkdownSegment[] = [];
  for (const segment of baseSegments) {
    if (segment.kind === 'markdown') {
      expanded.push(...expandMarkdownWithS3Attachments(segment, isStreaming));
    } else {
      expanded.push(segment);
    }
  }

  if (options.filePathsInOrder?.length) {
    return applyHistoryFilePaths(expanded, options.filePathsInOrder);
  }

  return expanded;
}

export function hasAgentMarkdownSegments(
  markdown: string,
  options: ParseAgentMarkdownSegmentsOptions = {}
): boolean {
  const normalized = rehydrateFlattenedTableRows(markdown);
  const hasTables = hasTableSegments(normalized);
  const hasS3 =
    !!options.enableS3Attachments && hasS3FileAttachmentSegments(normalized, !!options.isStreaming);
  const hasMetadataFiles =
    !!options.enableS3Attachments && (options.filePathsInOrder?.length ?? 0) > 0;
  return hasTables || hasS3 || hasMetadataFiles;
}

export function tableStructureKey(tableData: ParsedMarkdownTableData | null): string | null {
  if (tableData === null) return null;
  return `${tableData.columns.length}::${tableData.columns.join('||')}`;
}

/** Stable default column widths for streaming / initial layout (not content-measured). */
export function buildDefaultColumnWidths(columns: readonly string[]): number[] {
  return columns.map((columnName) => {
    if (isDocumentsHeavyColumn(columnName)) {
      return Math.min(RESIZE_MIN_DOCUMENTS_COLUMN, DEFAULT_DOCUMENTS_COLUMN_CAP);
    }
    if (isProseHeavyColumn(columnName)) {
      return Math.min(RESIZE_MIN_PROSE_COLUMN, DEFAULT_PROSE_COLUMN_CAP);
    }
    return DEFAULT_STREAMING_COLUMN_WIDTH;
  });
}

const INVALID_FILE_NAME_CHARS = new Set('<>:"/\\|?*');

function isInvalidFileNameChar(char: string): boolean {
  const code = char.charCodeAt(0);
  return INVALID_FILE_NAME_CHARS.has(char) || (code >= 0x00 && code <= 0x1f);
}

function removeTrailingDots(value: string): string {
  let normalized = value;
  while (normalized.endsWith('.')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

function replaceHtmlLineBreaks(value: string): string {
  let result = '';
  let index = 0;
  const lower = value.toLowerCase();
  while (index < value.length) {
    if (lower.startsWith('<br', index)) {
      let cursor = index + 3;
      while (cursor < value.length && (value[cursor] === ' ' || value[cursor] === '\t')) {
        cursor += 1;
      }
      if (cursor < value.length && value[cursor] === '/') {
        cursor += 1;
      }
      if (cursor < value.length && value[cursor] === '>') {
        result += '\n';
        index = cursor + 1;
        continue;
      }
    }
    result += value[index];
    index += 1;
  }
  return result;
}

function stripDelimitedSegments(value: string, opener: string, closer: string): string {
  let result = '';
  let index = 0;
  while (index < value.length) {
    if (value.startsWith(opener, index)) {
      const innerStart = index + opener.length;
      const closeIndex = value.indexOf(closer, innerStart);
      if (closeIndex > innerStart) {
        result += value.slice(innerStart, closeIndex);
        index = closeIndex + closer.length;
        continue;
      }
    }
    result += value[index];
    index += 1;
  }
  return result;
}

function stripMarkdownLinks(value: string): string {
  let result = '';
  let index = 0;
  while (index < value.length) {
    if (value[index] === '[') {
      const labelEnd = value.indexOf(']', index + 1);
      if (labelEnd > index && value[labelEnd + 1] === '(') {
        const urlEnd = value.indexOf(')', labelEnd + 2);
        if (urlEnd > labelEnd) {
          result += value.slice(index + 1, labelEnd);
          index = urlEnd + 1;
          continue;
        }
      }
    }
    result += value[index];
    index += 1;
  }
  return result;
}

function stripHtmlTags(value: string): string {
  let result = '';
  let index = 0;
  while (index < value.length) {
    if (value[index] === '<') {
      const closeIndex = value.indexOf('>', index + 1);
      if (closeIndex > index) {
        index = closeIndex + 1;
        continue;
      }
    }
    result += value[index];
    index += 1;
  }
  return result;
}

/** Selected plain-text cell values per column index (multi-select within a column). */
export type TableColumnFilters = Readonly<Record<number, ReadonlySet<string>>>;

export interface FilterTableRowsOptions {
  searchQuery?: string;
  columnFilters?: TableColumnFilters;
  /** When set, used for column filter option extraction and matching. */
  columnFilterCellValue?: (cell: string) => string;
}

/** Plain-text cell value used for search, filter, and export matching. */
export function getTableCellPlainText(cell: string): string {
  return stripTableCellForExport(cell ?? '');
}

/** Case-insensitive partial match against plain-text cell values (strips markdown/HTML). */
export function tableRowMatchesSearchQuery(row: readonly string[], query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }
  return row.some((cell) =>
    getTableCellPlainText(cell).toLowerCase().includes(normalizedQuery),
  );
}

export function tableRowMatchesColumnFilters(
  row: readonly string[],
  columnFilters: TableColumnFilters,
  columnFilterCellValue: (cell: string) => string = getTableCellPlainText,
): boolean {
  for (const [colIndexStr, selectedValues] of Object.entries(columnFilters)) {
    if (!selectedValues?.size) {
      continue;
    }
    const colIndex = Number(colIndexStr);
    const cellValue = columnFilterCellValue(row[colIndex] ?? '');
    if (!selectedValues.has(cellValue)) {
      return false;
    }
  }
  return true;
}

export function hasActiveTableColumnFilters(columnFilters: TableColumnFilters): boolean {
  return Object.values(columnFilters).some((values) => values.size > 0);
}

/** Unique plain-text values for a column, sorted A–Z. */
export function getUniqueColumnCellValues(
  tableData: ParsedMarkdownTableData,
  columnIndex: number,
  cellValueReader: (cell: string) => string = getTableCellPlainText,
): readonly string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const row of tableData.rows) {
    const value = cellValueReader(row[columnIndex] ?? '');
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    values.push(value);
  }
  return values.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * Filters table rows. Search and column filters combine with AND logic.
 * Within a column, multiple selected values combine with OR logic.
 */
export function filterTableRows(
  tableData: ParsedMarkdownTableData,
  options: FilterTableRowsOptions = {},
): readonly (readonly string[])[] {
  const searchQuery = options.searchQuery?.trim() ?? '';
  const columnFilters = options.columnFilters ?? {};
  const hasSearch = searchQuery.length > 0;
  const hasColumnFilters = hasActiveTableColumnFilters(columnFilters);

  if (!hasSearch && !hasColumnFilters) {
    return tableData.rows;
  }

  const columnFilterCellValue = options.columnFilterCellValue ?? getTableCellPlainText;

  return tableData.rows.filter((row) => {
    if (hasSearch && !tableRowMatchesSearchQuery(row, searchQuery)) {
      return false;
    }
    if (hasColumnFilters && !tableRowMatchesColumnFilters(row, columnFilters, columnFilterCellValue)) {
      return false;
    }
    return true;
  });
}

/**
 * Exact cell value for RIA column filters — preserves underscores and other
 * markdown punctuation that export/search stripping would remove.
 */
export function getTableCellFilterValue(cell: string): string {
  const { text: protectedCell, placeholders } = protectInsIdsInText(cell ?? '');
  let value = replaceHtmlLineBreaks(protectedCell);
  value = stripMarkdownLinks(value);
  value = stripHtmlTags(value);
  value = restoreProtectedInsIds(value, placeholders);
  return value.trim();
}

/** Plain-text value for spreadsheet export (strips markdown/HTML). */
export function stripTableCellForExport(cell: string): string {
  const { text: protectedCell, placeholders } = protectInsIdsInText(cell ?? '');
  let value = replaceHtmlLineBreaks(protectedCell);
  value = stripMarkdownLinks(value);
  value = stripDelimitedSegments(value, '**', '**');
  value = stripDelimitedSegments(value, '*', '*');
  value = stripDelimitedSegments(value, '__', '__');
  value = stripDelimitedSegments(value, '_', '_');
  value = stripDelimitedSegments(value, '`', '`');
  value = stripHtmlTags(value);
  value = restoreProtectedInsIds(value, placeholders);
  return value.trim();
}

/** Removes characters that are invalid in file names across common OS targets. */
export function sanitizeDownloadFileName(fileName: string): string {
  const withoutInvalidChars = [...fileName.trim()]
    .filter((char) => !isInvalidFileNameChar(char))
    .join('');
  const trimmed = removeTrailingDots(withoutInvalidChars);
  return trimmed.length > 0 ? trimmed : 'table';
}

/** Builds a 2D array (header row + data rows) for spreadsheet export. */
export function buildTableExportSheetData(tableData: ParsedMarkdownTableData): string[][] {
  const headers = tableData.columns.map((column) => stripTableCellForExport(column));
  const rows = tableData.rows.map((row) =>
    row.map((cell) => stripTableCellForExport(cell ?? '')),
  );
  return [headers, ...rows];
}

/** Writes parsed table data to an .xlsx file in the browser. */
export function exportParsedTableToXlsx(tableData: ParsedMarkdownTableData, fileName: string): void {
  const worksheet = XLSX.utils.aoa_to_sheet(buildTableExportSheetData(tableData));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  XLSX.writeFile(workbook, `${sanitizeDownloadFileName(fileName)}.xlsx`);
}
